import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, ShieldCheck, X, Loader2 } from "lucide-react";

// mode: 'create' (first-time set PIN then enter) | 'exit' (verify PIN to leave)
export function KidsPinModal({ mode, onClose, onSuccess }) {
  const { refreshUser } = useAuth();
  const len = 4;
  const [digits, setDigits] = useState(Array(len).fill(""));
  const [busy, setBusy] = useState(false);
  const inputs = useRef([]);

  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const pin = digits.join("");

  const setDigit = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < len - 1) inputs.current[i + 1]?.focus();
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const submit = async () => {
    if (pin.length !== len) return;
    setBusy(true);
    try {
      if (mode === "create") {
        await api.post("/kids/set-pin", { pin });
        await refreshUser();
        toast.success("Kids PIN set");
      } else {
        await api.post("/kids/unlock", { pin });
      }
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect PIN");
      setDigits(Array(len).fill(""));
      inputs.current[0]?.focus();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kids-pin-modal">
      <div className="bg-[#121824] border border-white/10 rounded-2xl p-8 w-full max-w-sm text-center fade-up relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={18} /></button>
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[var(--nik-blue)] to-[#0047B3] flex items-center justify-center mx-auto">
          {mode === "create" ? <ShieldCheck size={26} className="text-white" /> : <Lock size={26} className="text-white" />}
        </div>
        <h2 className="font-display text-xl font-bold text-white mt-4">
          {mode === "create" ? "Set a Kids PIN" : "Enter PIN to Exit"}
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          {mode === "create"
            ? "Create a 4-digit PIN. You'll need it to leave Kids mode."
            : "Ask a grown-up to enter the PIN to exit Kids mode."}
        </p>
        <div className="flex justify-center gap-3 mt-6">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              data-testid={`kids-pin-${i}`}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold bg-[#0B0E14] border border-white/15 rounded-xl text-white outline-none focus:border-[var(--nik-blue)]"
            />
          ))}
        </div>
        <button
          data-testid="kids-pin-submit"
          onClick={submit}
          disabled={busy || pin.length !== len}
          className="mt-6 w-full bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {mode === "create" ? "Set PIN & Enter" : "Unlock"}
        </button>
      </div>
    </div>
  );
}
